import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Alert, Box, Button, Card, CardContent, Link, Stack, TextField, Typography } from '@mui/material';
import { forgotPasswordRequest } from '../../api/auth';
import { forgotPasswordSchema, ForgotPasswordFormValues } from '../../validation/auth';

export const ForgotPasswordPage = () => {
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormValues>({ resolver: zodResolver(forgotPasswordSchema) });

  const onSubmit = async (values: ForgotPasswordFormValues) => {
    await forgotPasswordRequest(values.email);
    setSubmitted(true);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        px: 2,
      }}
    >
      <Card variant="outlined" sx={{ width: '100%', maxWidth: 400 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom>
            Forgot password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter your account email and we&apos;ll send you a reset link.
          </Typography>

          {submitted ? (
            <Alert severity="success">
              If an account exists for that email, a reset link has been sent.
            </Alert>
          ) : (
            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  type="email"
                  autoFocus
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  {...register('email')}
                />
                <Button type="submit" variant="contained" size="large" disabled={isSubmitting} fullWidth>
                  {isSubmitting ? 'Sending…' : 'Send reset link'}
                </Button>
              </Stack>
            </Box>
          )}

          <Link component={RouterLink} to="/login" variant="body2" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
            Back to sign in
          </Link>
        </CardContent>
      </Card>
    </Box>
  );
};
